import { type BatchData, type Contracts, type SubPurpose } from 'prove-common'
import { RJSFSchema } from 'prove-common/rjsf'

export interface TaskFormData {
  labId: number
  labReceptionAddressId?: number | null
  contractId?: number
  productId: number
  matrixId: number
  substanceIds: number[]
  quantity: number
  countSubSamples: boolean
  sampntTypeId: number
  payloadSchemaId: number
  ordningsverdiId: number
  subPurposeId?: number
  description?: string
  locationRequired: boolean
}

// Reusable type for oneOf entries that may be marked invalid
interface OneOfOption {
  const: number
  title: string
  'x-new-combination'?: true
}

export const getTaskSchema = (
  valid: BatchData,
  invalid: BatchData,
  contracts: Contracts,
  subPurposes: SubPurpose[] = []
): RJSFSchema => {
  const products = [...valid.products, ...invalid.products]
  const matrices = [...valid.matrices, ...invalid.matrices]
  const substances = [...valid.substances, ...invalid.substances]
  const payloadSchemas = [...valid.payloadSchemas, ...invalid.payloadSchemas]
  const labs = [...valid.labs, ...invalid.labs]
  const sampntTypes = valid.sampnts
  const ordningsverdier = valid.ordningsverdier
  const isNewCombinationId = <T extends { id: number }>(
    list: T[],
    id: number
  ) => list.some((x) => x.id === id)

  return {
    type: 'object',
    properties: {
      labId: {
        type: 'number',
        title: 'Laboratorium',
        description: 'Velg laboratorium som skal utføre analysen',
        oneOf: labs.map((lab) => {
          const title = lab.name
          const newCombinationFlag = isNewCombinationId(invalid.labs, lab.id)
          const option: OneOfOption = {
            const: lab.id,
            title
          }
          if (newCombinationFlag) option['x-new-combination'] = true
          return option
        })
      },
      contractId: {
        type: 'number',
        title: 'Kontrakt',
        description: 'Velg hvilken kontrakt planen gjelder for',
        oneOf: contracts?.flatMap((contract) => ({
          const: contract.id,
          title: contract.title
        }))
      },
      productId: {
        type: 'number',
        title: 'Produkt',
        description: 'Velg produktet prøven skal tas av',
        oneOf: products.map((product) => {
          const title = product.name ?? product.id.toString()
          const newCombinationFlag = isNewCombinationId(
            invalid.products,
            product.id
          )
          const option: OneOfOption = {
            const: product.id,
            title
          }
          if (newCombinationFlag) option['x-new-combination'] = true
          return option
        })
      },
      matrixId: {
        type: 'number',
        title: 'Prøve av',
        description: 'Velg hva prøven skal tas av',
        oneOf: matrices.map((matrix) => {
          const title = matrix.name ?? matrix.id.toString()
          const newCombinationFlag = isNewCombinationId(
            invalid.matrices,
            matrix.id
          )
          const option: OneOfOption = {
            const: matrix.id,
            title
          }
          if (newCombinationFlag) option['x-new-combination'] = true
          return option
        })
      },
      substanceIds: {
        type: 'array',
        title: 'Analyser for',
        description: 'Velg hva det skal analyseres for',
        default: [],
        minItems: 1,
        uniqueItems: true,
        items: {
          type: 'number',
          default: '',
          oneOf: substances.map((substance) => {
            const title = substance.name
            const newCombinationFlag = isNewCombinationId(
              invalid.substances,
              substance.id
            )
            const option: OneOfOption = {
              const: substance.id,
              title
            }
            if (newCombinationFlag) option['x-new-combination'] = true
            return option
          })
        }
      },
      payloadSchemaId: {
        type: 'number',
        title: 'Skjema',
        description: 'Velg skjema for prøvene',
        oneOf: payloadSchemas.map((payloadSchema) => {
          const title = payloadSchema.name ?? payloadSchema.id.toString()
          const newCombinationFlag = isNewCombinationId(
            invalid.payloadSchemas,
            payloadSchema.id
          )
          const option: OneOfOption = {
            const: payloadSchema.id,
            title
          }
          if (newCombinationFlag) option['x-new-combination'] = true
          return option
        })
      },
      sampntTypeId: {
        type: 'number',
        title: 'Uttakssted',
        description: 'Velg uttakssted',
        oneOf: sampntTypes?.map((sampntType) => ({
          const: sampntType.id,
          title: sampntType.nameNo
        }))
      },
      ordningsverdiId: {
        type: 'number',
        title: 'Arkivkategori',
        description: 'Velg i hvilken kategori prøvene skal arkiveres',
        oneOf: ordningsverdier?.map((ordningsverdi) => ({
          const: ordningsverdi.id,
          title: ordningsverdi.kategoriNavn
        }))
      },
      subPurposeId: {
        type: 'number',
        title: 'Underhensikt',
        description: 'Velg underhensikt for oppgaven',
        oneOf: subPurposes.map((subPurpose) => ({
          const: subPurpose.id,
          title: subPurpose.name
        }))
      },
      description: {
        type: 'string',
        title: 'Beskrivelse',
        description: 'Gi oppgaven en utfyllende beskrivelse'
      },
      locationRequired: {
        type: 'boolean',
        title: 'Prøver skal ha nøyaktig posisjon',
        description: 'Velg om prøven krever posisjon'
      },
      countSubSamples: {
        type: 'boolean',
        title: 'Hvordan telle antall prøver?',
        description: 'Velg hvordan antall prøver skal telles',
        oneOf: [
          {
            const: false,
            title: 'Prøveposer'
          },
          {
            const: true,
            title: 'Delprøver'
          }
        ]
      },
      quantity: {
        type: 'number',
        title: 'Antall prøver',
        description: 'Hvor mange prøver skal tas?',
        minimum: 0,
        maximum: 99999
      }
    },
    required: [
      'labId',
      'contractId',
      'matrixId',
      'productId',
      'substanceIds',
      'payloadSchemaId',
      'sampntTypeId',
      'ordningsverdiId',
      'countSubSamples',
      'quantity'
    ]
  }
}
